import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
	subscribeToPropertyDefs,
	savePropertyDefs,
	SCHEMA_SPACES,
} from '../services/PropertyDefsService';
import { activeDefs, normalizeDefs } from '../services/propertyDefsModel';

/**
 * The user-defined property schema for whichever space the surrounding task UI
 * is bound to.
 *
 * THE DEFAULT IS AN EMPTY, READ-ONLY SCHEMA, and that is what keeps this
 * invisible to Work. `propertyDefs` rules enumerate their keys and `work` is
 * deliberately absent, so the Work surface must never try to read or write one —
 * a component with no provider around it gets `defs: []` and `canEdit: false`,
 * renders nothing, and issues no query.
 *
 * Deliberately SEPARATE from TaskServiceContext even though both are keyed by
 * space. The task service is the collection a surface writes to; this is the
 * shape of what it writes. Merging them would mean every consumer of one
 * re-renders on changes to the other, and would tie the Work task surface to a
 * document it is forbidden to read.
 */

const EMPTY = {
	defs: [],
	activeDefs: [],
	canEdit: false,
	loading: false,
	error: null,
	save: async () => {},
};

const PropertyDefsContext = createContext(EMPTY);

/**
 * @param {string} props.space one of SCHEMA_SPACES; anything else (including
 *   'work') yields the empty read-only schema rather than an error.
 */
export function PropertyDefsProvider({ space, children }) {
	const supported = SCHEMA_SPACES.includes(space);

	const [defs, setDefs] = useState([]);
	const [loading, setLoading] = useState(supported);
	const [error, setError] = useState(null);

	useEffect(() => {
		if (!supported) {
			setDefs([]);
			setLoading(false);
			return undefined;
		}
		setLoading(true);
		return subscribeToPropertyDefs(
			space,
			(next) => {
				setDefs(next);
				setLoading(false);
				setError(null);
			},
			(err) => {
				setError(err);
				setLoading(false);
			}
		);
	}, [space, supported]);

	/* Takes the WHOLE list, because every mutator in propertyDefsModel returns a
	   whole list — the document is one small array and a partial write would
	   need merge semantics the model deliberately doesn't have. */
	const save = useCallback(
		async (nextDefs) => {
			if (!supported) return;
			await savePropertyDefs(space, normalizeDefs(nextDefs));
		},
		[space, supported]
	);

	const value = useMemo(
		() => ({
			defs,
			activeDefs: activeDefs(defs),
			canEdit: supported,
			loading,
			error,
			save,
		}),
		[defs, supported, loading, error, save]
	);

	return (
		<PropertyDefsContext.Provider value={value}>
			{children}
		</PropertyDefsContext.Provider>
	);
}

/** The property schema for the current surface. Empty and read-only by default. */
export function usePropertyDefs() {
	return useContext(PropertyDefsContext);
}

export { EMPTY as emptyPropertySchema };
export default PropertyDefsContext;
